import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Button, Group, Loader, Paper, Stack, Text, Tooltip} from '@mantine/core';
import {useMachine} from '@xstate/react';
import React, {useEffect, useMemo, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {
  FullScreenFlow,
  FullScreenFlowContent,
  FullScreenFlowFooter,
  FullScreenFlowHeader,
} from 'antibodies/nested-data-creation/full-screen/FullScreenFlowLayout';
import ProteinCreationFinalizeStep from 'antibodies/nested-data-creation/full-screen/ProteinCreationFinalizeStep';
import ProteinCreationFlowMachine from 'antibodies/nested-data-creation/full-screen/ProteinCreationFlowMachine';
import ProteinCreationLeaveModal from 'antibodies/nested-data-creation/full-screen/ProteinCreationLeaveModal';
import {
  clearProteinCreationSettings,
  doProteinCreationSettingsRequireReset,
  useProteinCreationSettings,
} from 'antibodies/nested-data-creation/full-screen/ProteinCreationSettingsUtil';
import ProteinCreationTable, {
  downloadProteinCreationTableCSV,
  getProteinCreationTableCSVDownloadDisabledReason,
} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTable';
import {
  mergeResolvedEntitiesIntoRows,
  summarizeRows,
  tableHasData,
} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTableUtil';
import useDnaPairEntityIds from 'antibodies/nested-data-creation/full-screen/useDnaPairEntityIds';
import useProteinCreationTableState from 'antibodies/nested-data-creation/full-screen/useProteinCreationTableState';
import {
  guardRelatedSchemasConfigIsValid,
  guardSelectedProjectFolderIsValid,
} from 'antibodies/nested-data-creation/ProteinCreationSharedUtil';
import {
  COMPONENT_AND_RELATED_SCHEMAS_FETCH_ERROR_MESSAGE,
  INVALID_PROTEIN_SCHEMA_ERROR_MESSAGE,
  SchemaFetchErrorKind,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import ValidationErrorsPane from 'antibodies/nested-data-creation/spreadsheet-import/panes/ValidationErrorsPane';
import {ANTIBODIES_ANALYTICS_ACTIONS} from 'constants/analytics-products/AntibodiesAnalyticsConstants';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import navigateToURL, {clearNavigationWarnings} from 'lib/navigateToURL';
import * as FullScreenActions from 'nav/full-screen/FullScreenActions';
import hasBlockingValidationErrors from 'notebook/structured-tables/util/hasBlockingValidationErrors';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import notifications from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import useWarnOnNavigate from 'ui-library/hooks/useWarnOnNavigate';
import * as logging from 'util/Logging';

const TEST_COMPONENT = 'ProteinCreationFlowPage';
const LEAVE_WARNING_MESSAGE = "Leave protein creation? Your progress creating proteins won't be saved.";

function getSchemaFetchErrorMessage(errorKind: SchemaFetchErrorKind): string {
  return errorKind === 'invalidSchema'
    ? INVALID_PROTEIN_SCHEMA_ERROR_MESSAGE
    : COMPONENT_AND_RELATED_SCHEMAS_FETCH_ERROR_MESSAGE;
}

function SchemaFetchErrorAlert({
  errorKind,
  onRetry,
}: {
  errorKind: SchemaFetchErrorKind;
  onRetry: () => void;
}): JSX.Element {
  // Only the generic fetch failure is retryable; an invalid schema keeps failing, so we omit Retry
  // and direct the user to pick a different schema instead.
  const isRetryable = errorKind === 'fetchFailed';
  return (
    <Alert color="red">
      <Stack align="flex-start" spacing="sm">
        <Text>{getSchemaFetchErrorMessage(errorKind)}</Text>
        {isRetryable ? (
          <Button variant="secondary" onClick={onRetry}>
            Retry
          </Button>
        ) : null}
      </Stack>
    </Alert>
  );
}

function ProteinCreationFlowPage(): JSX.Element {
  const [state, send] = useMachine(ProteinCreationFlowMachine, {input: {}});
  const {creationSettings} = state.context;
  const {available: componentAndRelatedSchemas, errorKind} = state.context.relatedComponentSchemas;
  const tableState = useProteinCreationTableState(creationSettings, componentAndRelatedSchemas);

  const [computedTable, setComputedTable] = useState<Array<Array<EvaluatedCell>> | null>(null);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);

  // The machine consumes the settings that opened the flow when it starts, so settings written afterwards
  // come from the settings modal and replace the current ones.
  const [pendingCreationSettings] = useProteinCreationSettings();
  const replaceRows = tableState?.replaceRows;
  useEffect(() => {
    if (pendingCreationSettings == null) {
      return;
    }
    if (doProteinCreationSettingsRequireReset(creationSettings, pendingCreationSettings)) {
      send({type: 'resetCreationSettings', creationSettings: pendingCreationSettings});
      replaceRows?.(null);
      setComputedTable(null);
    } else {
      send({type: 'updateCreationSettings', creationSettings: pendingCreationSettings});
    }
    clearProteinCreationSettings();
  }, [pendingCreationSettings, creationSettings, send, replaceRows]);

  // Merge table rows with computed values to include resolved entities
  const rows = useMemo(() => {
    const tableRows = tableState?.rows;
    const tableColumns = tableState?.structuredTable.columns;
    if (tableRows == null || tableColumns == null) {
      return [];
    }
    return mergeResolvedEntitiesIntoRows(tableRows, tableColumns, computedTable);
  }, [tableState?.rows, tableState?.structuredTable.columns, computedTable]);
  const hasUnsavedData = tableHasData(rows);
  useWarnOnNavigate(hasUnsavedData && !state.matches('done'), LEAVE_WARNING_MESSAGE);
  const {dnaPairEntityIds, isLoading: isLoadingDnaPairs} = useDnaPairEntityIds({
    rows,
    format: creationSettings.selectedFormat,
    startingType: creationSettings.selectedStartingType,
    dnaPairSchemaIds: state.context.relatedComponentSchemas.available?.dnaPairSchemaIds ?? null,
    shouldFetch: state.matches('finalize'),
  });
  const {
    hasData,
    nonEmptyRowCount: numberOfAntibodies,
    hasDnaInputs,
  } = useMemo(
    () =>
      summarizeRows(
        rows,
        creationSettings.selectedFormat,
        creationSettings.selectedStartingType,
        dnaPairEntityIds
      ),
    [rows, creationSettings.selectedFormat, creationSettings.selectedStartingType, dnaPairEntityIds]
  );
  const hasBlockingErrors = computedTable != null && hasBlockingValidationErrors(computedTable);
  const canAdvanceToFinalize = hasData && !hasBlockingErrors;

  const trackClosedFlow = (): void => {
    analytics.trackEventWithModule(
      ANTIBODIES_ANALYTICS_ACTIONS.CLOSED_PROTEIN_CREATION,
      ANALYTICS_MODULE.ANTIBODIES,
      {
        formatId: creationSettings.selectedFormat.id,
        startingDataType: creationSettings.selectedStartingType,
        step: state.value,
      }
    );
  };

  useEffect(() => {
    if (!state.matches('done')) {
      return;
    }
    analytics.trackEventWithModule(
      ANTIBODIES_ANALYTICS_ACTIONS.SUBMITTED_PROTEIN_CREATION,
      ANALYTICS_MODULE.ANTIBODIES,
      {
        formatId: state.context.creationSettings.selectedFormat.id,
        startingDataType: state.context.creationSettings.selectedStartingType,
        numberOfAntibodies,
      }
    );
    const {ndcWorkspaceUrl} = state.context;
    if (ndcWorkspaceUrl != null) {
      FullScreenActions.hideScreen();
      navigateToURL(ndcWorkspaceUrl);
    } else {
      logging.error('No NDC workspace URL returned from create proteins from table');
      notifications.info("We're working on creating proteins and components...");
      FullScreenActions.hideScreenForRouting();
    }
  }, [state, numberOfAntibodies]);

  const getNextButtonTooltip = (): string | undefined => {
    if (!hasData) {
      return 'Add at least one protein to continue';
    }
    if (hasBlockingErrors) {
      return 'Fix errors to continue';
    }
    return undefined;
  };

  return (
    <FullScreenFlow>
      <FullScreenFlowHeader
        title="Create proteins"
        subtitle={creationSettings.selectedSchemaName}
        onClose={() => {
          if (hasUnsavedData) {
            setIsLeaveModalOpen(true);
          } else {
            trackClosedFlow();
            FullScreenActions.hideScreenForRouting();
          }
        }}
      />
      {isLeaveModalOpen && tableState != null && (
        <ProteinCreationLeaveModal
          downloadDisabledReason={getProteinCreationTableCSVDownloadDisabledReason({
            structuredTable: tableState.structuredTable,
            isSchemaLoaded: tableState.isSchemaLoaded,
            computedTable,
          })}
          onDownloadCSV={() => {
            if (computedTable == null) {
              return;
            }
            downloadProteinCreationTableCSV({structuredTable: tableState.structuredTable, computedTable});
          }}
          onStay={() => {
            setIsLeaveModalOpen(false);
          }}
          onLeave={() => {
            setIsLeaveModalOpen(false);
            trackClosedFlow();
            // The user already confirmed in the modal, so skip the navigation warning prompt.
            clearNavigationWarnings();
            FullScreenActions.hideScreenForRouting();
          }}
        />
      )}
      <FullScreenFlowContent>
        {state.matches('initializing') &&
          (errorKind == null ? (
            <Loader aria-label="Loading schemas" />
          ) : (
            <SchemaFetchErrorAlert
              errorKind={errorKind}
              onRetry={() => {
                send({type: 'retry'});
              }}
            />
          ))}
        {state.matches('editing') &&
          (tableState != null ? (
            <ProteinCreationTable
              creationSettings={creationSettings}
              structuredTable={tableState.structuredTable}
              handleTableUpdate={tableState.handleTableUpdate}
              replaceRows={tableState.replaceRows}
              hasExistingRows={hasUnsavedData}
              isSchemaLoaded={tableState.isSchemaLoaded}
              computedTable={computedTable}
              onComputedTableChanged={setComputedTable}
            />
          ) : (
            <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load protein creation table" />
          ))}
        {state.matches('finalize') && (
          <ProteinCreationFinalizeStep
            rows={rows}
            creationSettings={creationSettings}
            hasDnaInputs={hasDnaInputs}
            dnaPairEntityIds={dnaPairEntityIds}
            projectFolderId={state.context.projectFolderId}
            onSetProjectFolder={(id) => {
              send({type: 'setProjectFolder', projectFolderId: id});
            }}
            relatedSchemasConfig={state.context.relatedComponentSchemas.selected}
            onSetRelatedSchemasConfig={(config) => {
              send({type: 'setRelatedSchemasConfig', relatedSchemasConfig: config});
            }}
            translationParameters={state.context.translationParameters}
            onSetTranslationParameters={(params) => {
              send({type: 'setTranslationParameters', translationParameters: params});
            }}
            componentAndRelatedSchemas={componentAndRelatedSchemas}
            componentAndRelatedSchemasErrorMessage={
              errorKind == null ? null : getSchemaFetchErrorMessage(errorKind)
            }
            selectedDocumentId={state.context.selectedDocumentId}
            onSetSelectedDocumentId={(id) => {
              send({type: 'setSelectedDocumentId', documentId: id});
            }}
          />
        )}
        {state.matches('validationErrors') && state.context.validationErrors != null && (
          <Paper bg="white" radius="md" p="md">
            <ValidationErrorsPane
              submissionValidationErrors={{
                validationErrors: state.context.validationErrors.rowErrors,
                batchErrors: state.context.validationErrors.batchErrors,
              }}
              rowIndexDisplayOffset={1}
            />
          </Paper>
        )}
      </FullScreenFlowContent>
      <FullScreenFlowFooter>
        <Group position="right">
          {state.matches('editing') && (
            <Tooltip
              label={getNextButtonTooltip()}
              disabled={canAdvanceToFinalize}
              // The sticky footer's stacking context sits below the table's sticky header and cells.
              withinPortal={true}
            >
              <span>
                <Button
                  onClick={() => {
                    send({type: 'next', hasData, hasBlockingErrors});
                  }}
                  disabled={!canAdvanceToFinalize}
                  {...getTestProps({component: TEST_COMPONENT, element: 'nextButton'})}
                >
                  Next
                </Button>
              </span>
            </Tooltip>
          )}
          {state.matches('finalize') && (
            <>
              <Button
                variant="tertiary"
                onClick={() => {
                  send({type: 'back'});
                }}
                {...getTestProps({component: TEST_COMPONENT, element: 'backButton'})}
              >
                Back
              </Button>
              <Button
                disabled={
                  isLoadingDnaPairs ||
                  !guardRelatedSchemasConfigIsValid(
                    state.context.relatedComponentSchemas.selected,
                    hasDnaInputs
                  ) ||
                  !guardSelectedProjectFolderIsValid(state.context.projectFolderId)
                }
                onClick={() => {
                  send({
                    type: 'submit',
                    rows,
                    hasDnaInputs,
                  });
                }}
                {...getTestProps({component: TEST_COMPONENT, element: 'submitButton'})}
              >
                Submit
              </Button>
            </>
          )}
          {state.matches('validationErrors') && (
            <Button
              variant="tertiary"
              onClick={() => {
                send({type: 'backToEditing'});
              }}
            >
              Back
            </Button>
          )}
        </Group>
      </FullScreenFlowFooter>
    </FullScreenFlow>
  );
}

export default ProteinCreationFlowPage;
